/* ============================================================
   Edition identity for the corridor benchmark corpus.

   /method has always shown the date the bands were struck. That answers
   "is this current" and not the question a reader in a meeting has,
   which is "are we looking at the same one". Two people can both be
   reading "as at 2 August 2026" while one of them has a service-worker
   cached copy from before four corridors were added — and this is an
   offline-first PWA, so a stale cached copy is the normal case rather
   than the edge one.

   A date is also not citable. "As at 2 August" tells a reader when
   someone looked, not what they were looking at. An edition tells them
   both:

     KLA-BENCH 2026.1 (9 corridors, 17 bands) · 4M9T

   The counts are a sanity check a reader can perform without leaving
   the page. The digest is derived from the bands themselves, so two
   readers quoting the same edition are quoting the same numbers, and a
   drifted copy announces itself without anyone having to remember to
   bump a version.

   Computed rather than declared for the same reason as the sister
   platform's regulatory index: a hand-maintained version is a promise
   someone keeps on every edit, and the cost of forgetting is two
   different corpora wearing one name — the precise failure a citable
   edition prevents.

   Not a security hash. FNV-1a in an alphabet with no characters that
   misread aloud. It detects drift and accident, not a forger.

   DUPLICATED, DELIBERATELY, AND THE COPY IS NAMED.

   The same construction — FNV-1a with offset 0x811c9dc5 and prime
   0x01000193, the same 32-character alphabet, the same explicit field
   separator — also lives in the sister platform, in
   assets/js/edition.js of the airline diagnostic toolkit, where it
   digests a regulatory corpus instead of a set of bands.

   They are separate repositories with no shared build, so there is
   nowhere honest to put one copy. Extracting a package to share forty
   lines would cost more than it saves and would couple two products
   that otherwise share nothing but a practice.

   What that costs is stated here rather than discovered later: a fix to
   the collision properties of one of these does NOT reach the other.
   If the separator, the alphabet or the hash changes here, the other
   file needs the same edit made by hand, and every published edition
   digest on both platforms changes with it. That is the price of the
   duplication and it is worth paying — but only while somebody knows
   they are paying it.
   ============================================================ */

import { BENCHMARKS, BENCHMARK_AS_OF } from './benchmarks.js';

/* Bumped by hand when the corpus changes in a way a reader should
   notice. The digest catches everything else, including the edits
   nobody thought to announce. */
export const EDITION_YEAR = 2026;
export const EDITION_SEQ = 1;

const ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/* An explicit separator rather than an empty join. Concatenating fields
   with nothing between them lets adjacent values collide — a low of
   0.05 followed by a median of 0.09 serialises identically to a low of
   0.0 and a median of 509 — and two different band sets would then wear
   the same digest. */
const FIELD_SEP = '|';

/* Every band across every corridor, in a fixed order.

   Takes the corpus as an argument rather than closing over the import,
   so a test can hand it a modified one. The first version could not:
   every assertion still passed with the digest hard-wired to a
   constant, because nothing could construct a second corpus to compare
   against. A digest whose change-sensitivity cannot be tested is a
   version number with extra steps. */
function eachBand(corpus, fn) {
  for (const key of Object.keys(corpus).sort()) {
    const corridor = corpus[key];
    for (const mode of Object.keys(corridor.modes || {}).sort()) {
      fn(key, corridor, mode, corridor.modes[mode]);
    }
  }
}

function canonicalCorpus(corpus, asOf) {
  const parts = [asOf];
  eachBand(corpus, (key, corridor, mode, band) => {
    parts.push(
      [
        key,
        mode,
        corridor.distance,
        band.low,
        band.median,
        band.high,
        band.provenance,
        band.payloadTonnes ?? '',
        band.measuredAt ?? corridor.measuredAt ?? '',
        corridor.asOf ?? ''
      ].join(FIELD_SEP)
    );
  });
  return parts.join(FIELD_SEP);
}

function shortHash(input) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  let out = '';
  for (let i = 0; i < 4; i += 1) out += ALPHABET[(hash >>> (i * 5)) & 31];
  return out;
}

/**
 * @returns {{label: string, year: number, seq: number, corridors: number,
 *            bands: number, digest: string, asOf: string}}
 */
export function benchmarkEdition(corpus = BENCHMARKS, asOf = BENCHMARK_AS_OF) {
  let bands = 0;
  eachBand(corpus, () => {
    bands += 1;
  });
  const corridors = Object.keys(corpus).length;
  const digest = shortHash(canonicalCorpus(corpus, asOf));

  return {
    year: EDITION_YEAR,
    seq: EDITION_SEQ,
    corridors,
    bands,
    digest,
    asOf,
    label: `KLA-BENCH ${EDITION_YEAR}.${EDITION_SEQ} (${corridors} corridors, ${bands} bands) · ${digest}`
  };
}

/** A line someone can paste into a board paper or a tender response. */
export function benchmarkCitation(edition, url) {
  const struck = new Date(edition.asOf).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return (
    `Kanda Logistics Advisory, Corridor Cost Benchmarks, ` +
    `edition ${edition.year}.${edition.seq} (${edition.digest}), ` +
    `${edition.corridors} corridors and ${edition.bands} bands, struck as at ${struck}. ` +
    `Road bands are derived from published rate guidance and are not a transaction dataset. ` +
    `Available at ${url}`
  );
}

/**
 * The corpus as rows, for export.
 * The caveat travels in the file: a band read out of a spreadsheet with
 * its provenance column stripped is exactly how "derived" becomes
 * "measured" in someone else's deck.
 */
export function benchmarkRows(corpus = BENCHMARKS) {
  const rows = [];
  eachBand(corpus, (key, corridor, mode, band) => {
    rows.push({
      corridor: key,
      name: corridor.name,
      mode,
      distanceKm: corridor.distance,
      currency: corridor.currency || 'USD',
      low: band.low,
      median: band.median,
      high: band.high,
      unit: 'per tonne-km',
      provenance: band.provenance,
      payloadTonnes: band.payloadTonnes ?? '',
      measuredAt: band.measuredAt ?? corridor.measuredAt ?? '',
      checkedAt: corridor.asOf ?? '',
      source: corridor.source || '',
      basis: band.basis || ''
    });
  });
  return rows;
}
